import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaChevronUp, FaQuestionCircle } from 'react-icons/fa';
import './QA.css';

const QA = () => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const qaData = [
    {
      question: "איזה שירותים את מציעה?",
      answer: "אני מציעה מגוון רחב של שירותים דיגיטליים: ניהול רשתות חברתיות (אינסטגרם, פייסבוק, טיקטוק), יצירת תוכן ויזואלי ומילולי, קמפיינים פרסומיים, צילומי תוכן, עיצוב גרפי, תפריטים לאירועים, עיצוב לוגו, מיתוג, וגרפיקות בודדות המותאמות לרשתות החברתיות, ניהול אישי ובניית תדמית לאישי ציבור. כל שירות מותאם אישית לצרכים הייחודיים של העסק שלכם."
    },
    {
      question: "איך קובעים שיחת התאמה?",
      answer: "שיחת היכרות והתאמה חשובה מאוד - היא מאפשרת לי להבין לעומק את צורכי העסק שלכם, התקציב והציפיות. על בסיס השיחה, אני בונה עבורכם חבילה מותאמת אישית שמתאימה בדיוק למידותיו הייחודיות של העסק. השיחה ללא עלות! ניתן ליצור קשר בטלפון או בווטסאפ לתיאום."
    },
    {
      question: "תוך כמה זמן רואים תוצאות?",
      answer: "על מנת לבנות תדמית שיווקית נכונה ומיטבית למותג בצורה אורגנית שתניב תוצאות - התוצאות לא מיידיות. אני מלווה בכל שלב ושלב, נעקוב אחר הנתונים ונשנה שיטות וגישות שיווקיות במידת הצורך. בדרך כלל תוצאות ראשוניות נראות תוך 4-6 שבועות, ותוצאות משמעותיות תוך 2-3 חודשים."
    },
    {
      question: "איפה את מספקת את השירותים שלך?",
      answer: "אני מספקת את שירותיי בכל הארץ מצפון ועד דרום! כמו כן, קיימת אופציייה של הוספת ימי צילום וימי יצירת תוכן לעסק. בנוסף לכך, קיימת גם אפשרות לקביעת פגישות דרך הזום לעסקים מעבר לים. אני מתאימה את השירות לכל מיקום ומעניקה שירות מקצועי בכל מקום בארץ ומחוצה לה."
    },
    {
      question: "איך נראה תהליך הליווי?",
      answer: "התהליך מתחיל בשיחת היכרות חינמית. ולאחר מכן, התהליך נבנה לפי ארבעה שלבים עיקריים, ובניהם:",
      list: [
        "בניית אסטרטגיה מותאמת אישית.",
        "יצירת תוכן ויזואלי ומילולי איכותי.", 
        "פרסום וניהול שוטף של הרשתות.",
        "מעקב אחר נתונים והתאמות לפי הצורך. טרנדים לעסק - מעודכנת בדברים החמים של הרשת!"
      ],
      listType: "numbered",
      footer: "לאורך כל הדרך אני נותנת ליווי אישי וזמינה לכל שאלה."
    },
    {
      question: "למה דווקא אני?",
      answer: "",
      list: [
        "נסיון רב שנים - 8 שנות ניסיון מוכח בתחום הדיגיטל.",
        "שירות אישי - יחס אישי וליווי צמוד לכל לקוח.",
        "הבאת טרנדים חדשניים לעסק - תמיד מעודכנת בחידושים האחרונים.",
        "אופי צעיר, תוסס ומלא בהשראה - אנרגיה חיובית שמשתקפת בעבודה.",
        "תוצאות מוכחות - פורטפוליו עשיר של לקוחות מרוצים ותוצאות מדידות."
      ]
    }
  ];

  return (
    <section id="qa" className="qa section" ref={sectionRef}>
      <div className="container">
        <div className={`qa-header ${isVisible ? 'fade-in' : ''}`}>
          <div className="section-icon">
            <FaQuestionCircle />
          </div>
          <h2 className="section-title gradient-text ornamental-text">
            שאלות נפוצות
          </h2>
          <p className="section-subtitle">
            תשובות לשאלות הנפוצות ביותר על השירותים הדיגיטליים שלי
          </p>
        </div>

        <div className={`qa-content ${isVisible ? 'fade-in' : ''}`}>
          <div className="qa-accordion">
            {qaData.map((item, index) => (
              <div 
                key={index} 
                className={`qa-item ${activeIndex === index ? 'active' : ''}`}
              >
                <button
                  className="qa-question"
                  onClick={() => toggleAccordion(index)}
                  aria-expanded={activeIndex === index}
                >
                  <span className="question-text">{item.question}</span>
                  <span className="question-icon">
                    {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                  </span>
                </button>
                <div className={`qa-answer ${activeIndex === index ? 'open' : ''}`}>
                  <div className="answer-content">
                    {item.answer && <p>{item.answer}</p>}
                    {item.list && (
                      <ul className={`answer-list ${item.listType === 'numbered' ? 'numbered-list' : ''}`}>
                        {item.list.map((listItem, listIndex) => (
                          <li key={listIndex} className="answer-list-item">
                            {listItem}
                          </li>
                        ))}
                      </ul>
                    )}
                    {item.footer && <p className="answer-footer">{item.footer}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default QA;
